<template>
  <div class="rb">
    <!-- Separate-response-per-farm: expandable farmer -> farm -> responses -->
    <table v-if="isSeparateFarm" class="rb-table">
      <thead>
        <tr>
          <th>{{ $t('surveyBuilder.farmerNameColumn') }}</th>
          <th style="width: 200px">{{ $t('surveyBuilder.totalFarms') || 'Total Farms' }}</th>
          <th style="width: 44px"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(farmer, fi) in farmers">
          <tr :key="'f' + fi" class="rb-farmer" @click="toggle(farmer.key)">
            <td>
              <span class="rb-name">{{ farmer.name || '—' }}</span>
            </td>
            <td>{{ farmer.totalFarms }}</td>
            <td class="rb-chevron-cell">
              <v-icon size="20" color="#787878">{{ isOpen(farmer.key) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            </td>
          </tr>
          <tr v-if="isOpen(farmer.key)" :key="'d' + fi" class="rb-detail">
            <td colspan="3">
              <table class="rb-inner">
                <thead>
                  <tr>
                    <th>{{ $t('surveyBuilder.farmColumn') }}</th>
                    <th>{{ $t('esgPlatform.responses') }}</th>
                    <th style="width: 240px">{{ $t('surveyBuilder.submittedDate') || 'Submitted Date' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(farm, ki) in farmer.farms">
                    <tr v-for="(resp, ri) in farm.responses" :key="ki + '-' + ri">
                      <td v-if="ri === 0" :rowspan="farm.responses.length" class="rb-farmname">{{ farm.farmName }}</td>
                      <td class="rb-top"><answer-block :question="synth(resp.response, resp)" /></td>
                      <td class="rb-top rb-date">{{ formatDate(resp.submittedOn) }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <!-- Single response per respondent -->
    <table v-else class="rb-table">
      <thead>
        <tr>
          <th>{{ nameColumnLabel || $t('surveyBuilder.farmerNameColumn') }}</th>
          <th>{{ $t('esgPlatform.responses') }}</th>
          <th style="width: 240px">{{ $t('surveyBuilder.submittedDate') || 'Submitted Date' }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(e, i) in entries" :key="i">
          <td>
            <span class="rb-name">{{ e.submittedByName || '—' }}</span>
          </td>
          <td class="rb-top"><answer-block :question="synth(e.response, e)" /></td>
          <td class="rb-top rb-date">{{ formatDate(e.submittedOn) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from "moment";
import ResponseAnswerBlock from "./ResponseAnswerBlock.vue";

export default {
  name: "RespondentBreakdown",
  components: { "answer-block": ResponseAnswerBlock },
  props: {
    entries: { type: Array, default: () => [] },
    isSeparateFarm: { type: Boolean, default: false },
    questionType: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    nameColumnLabel: { type: String, default: "" },
  },
  data() {
    return { expanded: {} };
  },
  computed: {
    // Group entries by farmer -> farm -> responses (sorted newest first).
    farmers() {
      const map = {};
      const order = [];
      for (const e of this.entries) {
        const key = String(e.submittedById || e.submittedByName || "unknown");
        if (!map[key]) {
          map[key] = { key, name: e.submittedByName, farmsMap: {}, farmOrder: [] };
          order.push(key);
        }
        const farmName = e.farmName || "-";
        if (!map[key].farmsMap[farmName]) {
          map[key].farmsMap[farmName] = [];
          map[key].farmOrder.push(farmName);
        }
        map[key].farmsMap[farmName].push(e);
      }
      return order.map((key) => {
        const f = map[key];
        const farms = f.farmOrder.map((fn) => ({
          farmName: fn,
          responses: f.farmsMap[fn]
            .slice()
            .sort((a, b) => new Date(b.submittedOn || 0) - new Date(a.submittedOn || 0)),
        }));
        return { key, name: f.name, totalFarms: farms.length, farms };
      });
    },
  },
  methods: {
    toggle(key) {
      this.$set(this.expanded, key, !this.isOpen(key));
    },
    isOpen(key) {
      return !!this.expanded[key];
    },
    synth(response, entry) {
      return {
        questionType: this.questionType,
        options: this.options || [],
        dimitraModule: (entry && entry.dimitraModule) || null,
        response,
        responses: [],
      };
    },
    formatDate(d) {
      return d ? moment(d).format("DD MMMM YYYY, hh:mm A") : "—";
    },
  },
};
</script>

<style scoped>
.rb-table {
  width: 100%;
  border-collapse: collapse;
}
.rb-table > thead > tr > th {
  text-align: left;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
  background: #00bd73;
  border-bottom: 1px solid #eceff3;
}
.rb-table > tbody > tr > td {
  padding: 12px 16px;
  font-size: 14px;
  color: #1e1e1e;
  border-bottom: 1px solid #f0f0f0;
}
.rb-farmer {
  cursor: pointer;
}
.rb-farmer:hover {
  background: #fafbfc;
}
.rb-name {
  font-weight: 600;
  vertical-align: middle;
}
.rb-chevron-cell {
  text-align: right;
}
.rb-detail > td {
  padding: 0 !important;
  background: #fafbfc;
}
.rb-inner {
  width: 100%;
  border-collapse: collapse;
}
.rb-inner thead th {
  text-align: left;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  color: #9aa0a6;
  border-bottom: 1px solid #eceff3;
}
.rb-inner tbody td {
  padding: 10px 16px;
  font-size: 14px;
  color: #444;
  border-bottom: 1px solid #f2f4f6;
  vertical-align: top;
}
.rb-farmname {
  font-weight: 500;
  color: #1e1e1e;
}
.rb-date {
  color: #5c5959;
  white-space: nowrap;
}
.rb-top {
  vertical-align: top;
}
</style>
